import "@/styles/froala.css";
import "@/styles/project-cover.css";
import "@/styles/participant-header.css";
import "@/styles/places.css";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { PropsWithChildren, ReactElement, ReactNode, useEffect } from "react";

import merge from "lodash/merge";

import { AppProps } from "next/app";

import NextNprogress from "nextjs-progressbar";

import { NextPage } from "next";

import NextHead from "next/head";

import { TranslationsProvider } from "@eo-locale/react";
import { Provider } from "react-redux";

import {
  extendTheme,
  ChakraProvider,
  theme as chakraUITheme
} from "@/storybook-components";
import { wrapper } from "@/redux/store/store";

import { yupConfig } from "@/lib";

import { useAppSelector } from "@/redux/store/hooks";
import { getEhqTheme } from "@/helpers/theme";
import useLiquidTemplateStyles from "@/hooks/useliquidTemplateStyles";

import { injectHeadCode } from "@/helpers/inject-head-code";

import { EOLocales } from "@/helpers/locale";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

type EmotionCacheProviderProps = PropsWithChildren<unknown> & {
  nonce: string;
};

export const EmotionCacheProvider = ({
  nonce,
  children
}: EmotionCacheProviderProps): ReactElement => {
  const cache = createCache({ key: "ehq-contentsecuritypolicy", nonce });
  return <CacheProvider value={cache}>{children}</CacheProvider>;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  yupConfig(); // configure yup with translations

  const getLayout = Component.getLayout ?? ((page) => page);

  const { theme } = useAppSelector((state) => state.theme);
  const { nonce } = useAppSelector((state) => state.adapter.adapter);

  const { homePageRevision } = useAppSelector(
    (state) => state.homePageRevision
  );
  const liquidTemplateStylesNode = useLiquidTemplateStyles();
  const { customHeadCode, customTrackingCode } = useAppSelector(
    (state) => state.settings.settings
  );

  const { ehqTheme } = getEhqTheme(theme, homePageRevision);

  useEffect(
    () => injectHeadCode(customHeadCode, nonce),
    [customHeadCode, nonce]
  );

  const { locale } = useAppSelector((state) => state.site.site);

  return (
    <>
      <NextHead>
        {liquidTemplateStylesNode && (
          <style
            dangerouslySetInnerHTML={{ __html: liquidTemplateStylesNode }}
          />
        )}
      </NextHead>
      <NextNprogress nonce={nonce} showOnShallow={false} />
      <EmotionCacheProvider nonce={nonce}>
        <ChakraProvider theme={extendTheme(merge(chakraUITheme, ehqTheme))}>
          <TranslationsProvider language={locale} locales={EOLocales}>
            {getLayout(<Component {...pageProps} />)}
          </TranslationsProvider>
        </ChakraProvider>
      </EmotionCacheProvider>
      {customTrackingCode && (
        <div dangerouslySetInnerHTML={{ __html: customTrackingCode }} />
      )}
    </>
  );
};

const WrappedApp = (props: AppPropsWithLayout) => {
  const { store, props: appProps } = wrapper.useWrappedStore(props);
  return (
    <Provider store={store}>
      <App {...appProps} />
    </Provider>
  );
};

export default WrappedApp;
